import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target, Users, Calendar, BarChart3, XCircle, TrendingUp, TrendingDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { targetAchievementApi } from '../../../api/crm/targets/targetAchievementApi';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import Button from '../../../components/common/Button/Button';

const TargetsDashboard = ({
  onExport,
  targets = [],
  setTargets
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [teamSummary, setTeamSummary] = useState([]);
  const [targetTypeSummaries, setTargetTypeSummaries] = useState(null);
  const [recentSchedules, setRecentSchedules] = useState([]);
  const [recentTargets, setRecentTargets] = useState([]);

  const [dateRange, setDateRange] = useState('last_30_days');
  const [customRange, setCustomRange] = useState({ startDate: '', endDate: '' });

  // Sorting state for recent targets
  const [targetSortColumn, setTargetSortColumn] = useState('name');
  const [targetSortDirection, setTargetSortDirection] = useState('asc');

  // Sorting state for recent schedules
  const [scheduleSortColumn, setScheduleSortColumn] = useState('nextRun');
  const [scheduleSortDirection, setScheduleSortDirection] = useState('asc');

  // Real data from API - no mock data needed

  // API Integration Functions
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const isCustom = Boolean(customRange.startDate && customRange.endDate);
      const finalRange = isCustom ? 'custom' : dateRange;

      const params = {
        range: finalRange,
        ...(isCustom && { startDate: customRange.startDate, endDate: customRange.endDate })
      };
      const response = await targetAchievementApi.targets.getDashboard(params);

      // Handle response structure - response is already response.data from interceptor
      // Backend returns: { responseCode: 200, responseMessage: "...", data: { kpis: {...}, teamSummary: [...], recentSchedules: [...] } }
      if (response && response.responseCode === 200 && response.data) {
        const payload = response.data;
        setDashboardData(payload);
        if (Array.isArray(payload.teamSummary)) {
          setTeamSummary(payload.teamSummary);
        } else {
          setTeamSummary([]);
        }
        if (payload.targetTypeSummaries) {
          setTargetTypeSummaries(payload.targetTypeSummaries);
        } else {
          setTargetTypeSummaries(null);
        }
        if (Array.isArray(payload.recentSchedules)) {
          setRecentSchedules(payload.recentSchedules);
        } else {
          setRecentSchedules([]);
        }
        if (Array.isArray(payload.recentTargets)) {
          setRecentTargets(payload.recentTargets);
        } else {
          setRecentTargets([]);
        }
      } else if (response && response.kpis) {
        // Fallback: if response structure is already the data object (kpis at root)
        setDashboardData(response);
        if (Array.isArray(response.teamSummary)) {
          setTeamSummary(response.teamSummary);
        } else {
          setTeamSummary([]);
        }
        if (response.targetTypeSummaries) {
          setTargetTypeSummaries(response.targetTypeSummaries);
        } else {
          setTargetTypeSummaries(null);
        }
        if (Array.isArray(response.recentSchedules)) {
          setRecentSchedules(response.recentSchedules);
        } else {
          setRecentSchedules([]);
        }
        if (Array.isArray(response.recentTargets)) {
          setRecentTargets(response.recentTargets);
        } else {
          setRecentTargets([]);
        }
      } else {
        setDashboardData(null);
        setTeamSummary([]);
        setTargetTypeSummaries(null);
        setRecentSchedules([]);
      }
    } catch (error) {
      setDashboardData(null);
      setTeamSummary([]);
      setTargetTypeSummaries(null);
      setRecentSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when period filter changes
  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, customRange.startDate, customRange.endDate]);


  const formatCount = (count) => {
    if (!count) return '0';
    return new Intl.NumberFormat('en-IN').format(count);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).replace(',', '');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getTargetTypeLabel = (type) => {
    switch (type) {
      case 'revenue':
        return 'Revenue';
      case 'deals':
        return 'Deal Count';
      case 'newOpps':
        return 'New Opportunities';
      default:
        return type;
    }
  };

  // Handle target sorting
  const handleTargetSort = (column) => {
    if (targetSortColumn === column) {
      setTargetSortDirection(targetSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setTargetSortColumn(column);
      setTargetSortDirection('asc');
    }
  };

  // Get sorted recent targets
  const getSortedRecentTargets = () => {
    if (!targetSortColumn || !recentTargets || recentTargets.length === 0) return recentTargets;

    return [...recentTargets].sort((a, b) => {
      let aValue, bValue;

      if (targetSortColumn === 'name') {
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
      } else if (targetSortColumn === 'assigneeName') {
        aValue = (a.assigneeName || '').toLowerCase();
        bValue = (b.assigneeName || '').toLowerCase();
      } else if (targetSortColumn === 'type') {
        aValue = (a.type || '').toLowerCase();
        bValue = (b.type || '').toLowerCase();
      } else if (targetSortColumn === 'progress') {
        aValue = Number(a.achievementPercentage) || 0;
        bValue = Number(b.achievementPercentage) || 0;
      } else if (targetSortColumn === 'achievement') {
        aValue = Number(a.achieved) || 0;
        bValue = Number(b.achieved) || 0;
      } else {
        aValue = a[targetSortColumn] || '';
        bValue = b[targetSortColumn] || '';
      }

      const order = targetSortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  // Handle schedule sorting
  const handleScheduleSort = (column) => {
    if (scheduleSortColumn === column) {
      setScheduleSortDirection(scheduleSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setScheduleSortColumn(column);
      setScheduleSortDirection('asc');
    }
  };

  // Get sorted recent schedules
  const getSortedRecentSchedules = () => {
    if (!scheduleSortColumn || !recentSchedules || recentSchedules.length === 0) return recentSchedules;

    return [...recentSchedules].sort((a, b) => {
      let aValue, bValue;

      if (scheduleSortColumn === 'name') {
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
      } else if (scheduleSortColumn === 'frequency') {
        aValue = (a.frequency || '').toLowerCase();
        bValue = (b.frequency || '').toLowerCase();
      } else if (scheduleSortColumn === 'nextRun') {
        aValue = new Date(a.nextRun || 0).getTime();
        bValue = new Date(b.nextRun || 0).getTime();
      } else if (scheduleSortColumn === 'enabled') {
        aValue = a.enabled ? 1 : 0;
        bValue = b.enabled ? 1 : 0;
      } else {
        aValue = a[scheduleSortColumn] || '';
        bValue = b[scheduleSortColumn] || '';
      }

      const order = scheduleSortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };



  const getPerformanceColor = (percentage) => {
    if (percentage >= 100) return 'text-green-600';
    if (percentage >= 90) return 'text-blue-600';
    if (percentage >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressBarColor = (percentage) => {
    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 90) return 'bg-blue-500';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const handleExport = async (format) => {
    try {

      // Call export API
      const response = await targetAchievementApi.targets.exportTargets(format);

      if (response.responseCode === 200) {
        // Create download link
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `targets_export_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

      } else {
        alert('Export failed: ' + response.responseMessage);
      }
    } catch (error) {
      alert('Export error: ' + error.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Targets Dashboard</h2>
          <p className="text-sm text-gray-600">
            Target-driven performance overview of created targets and achievements
          </p>
        </div>


        <div className="flex items-center space-x-3">
          {(!customRange.startDate && !customRange.endDate) && (
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all_time">All Time</option>
              <option value="today">Today</option>
              <option value="last_7_days">Last 7 days</option>
              <option value="last_30_days">Last 30 days</option>
              <option value="last_60_days">Last 60 days</option>
              <option value="last_90_days">Last 90 days</option>
            </select>
          )}

          <DateRangeFilter
            startDate={customRange.startDate}
            endDate={customRange.endDate}
            onChange={(newRange) => setCustomRange((prev) => ({ ...prev, ...newRange }))}
            allowFutureDates={false}
          />

          {(customRange.startDate || customRange.endDate) && (
            <Button
              variant="outline"
              onClick={() => {
                setCustomRange({ startDate: '', endDate: '' });
                setDateRange('last_30_days');
              }}
            >
              Clear
            </Button>
          )}

          {/* Actions */}
          <div className="flex items-center space-x-2 pl-2">

          </div>
        </div>
      </div>

      {loading ? (
        <>
          {/* KPI Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
                <div className="h-8 bg-gray-200 rounded w-20"></div>
              </div>
            ))}
          </div>

          {/* Target-Type Summaries Skeleton */}
          <div className="bg-white rounded-lg shadow-sm p-6 animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="h-5 bg-gray-200 rounded w-32 mb-3"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-4 bg-gray-200 rounded w-28"></div>
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                    <div className="h-2 bg-gray-200 rounded w-full mt-2"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Team Summary Skeleton - Table Format */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="h-6 bg-gray-200 rounded w-48"></div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="h-4 bg-gray-200 rounded w-28"></div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="h-4 bg-gray-200 rounded w-36"></div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 bg-gray-200 rounded w-28"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 bg-gray-200 rounded w-28"></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Schedules Skeleton */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="h-6 bg-gray-200 rounded w-40"></div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="h-4 bg-gray-200 rounded w-28"></div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* KPI Cards - Target-driven */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Active Targets */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Active Targets</span>
                <Target className="h-4 w-4 text-gray-400" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">
                  {dashboardData?.kpis?.totalActiveTargets ?? 0}
                </span>
              </div>
            </div>

            {/* Revenue Targets */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Revenue Targets</span>
                <TrendingUp className="h-4 w-4 text-gray-400" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">
                  {dashboardData?.kpis?.revenueTargetCount ?? 0}
                </span>
              </div>
            </div>

            {/* Deal Targets */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Deal Targets</span>
                <Users className="h-4 w-4 text-gray-400" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">
                  {dashboardData?.kpis?.dealTargetCount ?? 0}
                </span>
              </div>
            </div>

            {/* Opportunity Targets */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Opportunity Targets</span>
                <BarChart3 className="h-4 w-4 text-gray-400" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">
                  {dashboardData?.kpis?.opportunityTargetCount ?? 0}
                </span>
              </div>
            </div>
          </div>


          {/* Target Performance by Team - List Format (Top 5) - Consolidated Target Details Column */}
          {/* Target Performance - Most Recent Targets (Top 5) */}
          {recentTargets.length > 0 && (() => {
            return (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Target Performance</h3>
                    <button
                      onClick={() => navigate('/created_targets')}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      type="button"
                    >
                      View All
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleTargetSort('name')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Target Name</span>
                            {targetSortColumn === 'name' ? (
                              targetSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                            ) : (
                              <ArrowUpDown className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleTargetSort('assigneeName')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Assignee</span>
                            {targetSortColumn === 'assigneeName' ? (
                              targetSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                            ) : (
                              <ArrowUpDown className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleTargetSort('type')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Type</span>
                            {targetSortColumn === 'type' ? (
                              targetSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                            ) : (
                              <ArrowUpDown className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleTargetSort('progress')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Progress</span>
                            {targetSortColumn === 'progress' ? (
                              targetSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                            ) : (
                              <ArrowUpDown className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleTargetSort('achievement')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Achievement</span>
                            {targetSortColumn === 'achievement' ? (
                              targetSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                            ) : (
                              <ArrowUpDown className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getSortedRecentTargets().map((target) => (
                        <tr key={target.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 truncate max-w-xs capitalize" title={target.name}>
                              {target.name}
                            </div>
                            <div className="text-xs text-gray-500 capitalize">
                              {target.scope} Target
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {(() => {
                              let list = [];
                              if (target.assigneesInfo && target.assigneesInfo.length > 0) {
                                list = target.assigneesInfo.map(m => ({
                                  name: m.name || 'Unknown',
                                  email: m.email || ''
                                }));
                              } else if (target.assigneeInfo) {
                                list = [{
                                  name: target.assigneeInfo.name || target.assigneeName || 'Unknown',
                                  email: target.assigneeInfo.email || ''
                                }];
                              } else {
                                list = [{
                                  name: target.assigneeName || 'Unknown',
                                  email: ''
                                }];
                              }

                              const hasMultiple = list.length > 1;
                              if (hasMultiple) {
                                const firstAssignee = list[0];
                                return (
                                  <div className="relative group inline-block">
                                    <span className="flex items-start space-x-1 cursor-help hover:text-blue-600 transition-colors">
                                      <span className="flex flex-col leading-tight">
                                        <span className="font-medium text-gray-900">{firstAssignee.name}</span>
                                        {firstAssignee.email && <span className="text-xs text-gray-500">{firstAssignee.email}</span>}
                                      </span>
                                      <span className="inline-flex items-center px-1.5 py-0.25 rounded-md text-xs font-bold bg-blue-50 text-blue-600 border border-blue-200 mt-0.5">
                                        +{list.length - 1}
                                      </span>
                                    </span>
                                    {/* Hover Tooltip Popover */}
                                    <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-3 min-w-[260px] transition-all duration-200">
                                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 border-b border-gray-100 pb-1">
                                        Team Assignees ({list.length})
                                      </div>
                                      <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                                        {list.map((member, idx) => (
                                          <div key={idx} className="flex flex-col text-xs leading-normal">
                                            <span className="font-semibold text-gray-800">{member.name}</span>
                                            {member.email && <span className="text-gray-500 font-normal">{member.email}</span>}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              } else {
                                const name = list[0]?.name || 'Unknown';
                                const email = list[0]?.email || '';
                                return (
                                  <div className="flex flex-col leading-tight">
                                    <span className="font-medium text-gray-900">{name}</span>
                                    {email && <span className="text-xs text-gray-500">{email}</span>}
                                  </div>
                                );
                              }
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600 capitalize">
                              {getTargetTypeLabel(target.type)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-[80px]">
                                <div
                                  className={`h-2 rounded-full ${getProgressBarColor(target.achievementPercentage || 0)}`}
                                  style={{ width: `${Math.min(target.achievementPercentage || 0, 100)}%` }}
                                ></div>
                              </div>
                              <span className={`text-xs font-semibold ${getPerformanceColor(target.achievementPercentage || 0)}`}>
                                {(target.achievementPercentage || 0).toFixed(1)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {target.type === 'revenue' ? `₹${formatIndianAmount(target.achieved)}` : formatCount(target.achieved)}
                            </div>
                            <div className="text-xs text-gray-500">
                              of {target.type === 'revenue' ? `₹${formatIndianAmount(target.value)}` : formatCount(target.value)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Recent Schedules Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Recent Schedules</h3>
                <button
                  onClick={() => navigate('/schedules')}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  type="button"
                >
                  View All
                </button>
              </div>
            </div>
            {recentSchedules.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleScheduleSort('name')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Schedule Name</span>
                          {scheduleSortColumn === 'name' ? (
                            scheduleSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleScheduleSort('frequency')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Frequency</span>
                          {scheduleSortColumn === 'frequency' ? (
                            scheduleSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleScheduleSort('nextRun')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Next Run</span>
                          {scheduleSortColumn === 'nextRun' ? (
                            scheduleSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleScheduleSort('enabled')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {scheduleSortColumn === 'enabled' ? (
                            scheduleSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getSortedRecentSchedules().map((schedule) => {
                      const scheduleId = schedule.id || schedule._id || schedule.scheduleId;
                      const frequencyText = schedule.frequency === 'weekly' && schedule.day
                        ? `${schedule.frequency} (${schedule.day})`
                        : schedule.frequency;

                      return (
                        <tr key={scheduleId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={schedule.name}>
                              {schedule.name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 capitalize">
                              {frequencyText}
                            </div>
                            <div className="text-xs text-gray-500">
                              {schedule.time}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {schedule.nextRun ? formatDateTime(schedule.nextRun) : 'N/A'}
                            </div>
                            {schedule.lastRun && (
                              <div className="text-xs text-gray-500">
                                Last: {formatDateTime(schedule.lastRun)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${schedule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                }`}
                            >
                              {schedule.enabled ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center">
                <Calendar className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No schedules found</p>
                <button
                  onClick={() => navigate('/schedules')}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  type="button"
                >
                  Create Schedule
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TargetsDashboard;

